import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Edit2, Trash2, GripVertical } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import ProgressStepModal from '../components/ProgressStepModal';
import { progressStepService } from '../services/progressStepService';
import { ProgressStep } from '../types/progressStep';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';

interface Project {
  _id: string;
  name: string;
  isProgressEnabled: boolean;
  progressLabelText: string;
}

const ProjectProgress: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();

  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [steps, setSteps] = useState<ProgressStep[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingStep, setEditingStep] = useState<ProgressStep | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [stepToDelete, setStepToDelete] = useState<ProgressStep | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Form loading state
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    if (workspaceId) {
      fetchProjects();
    }
  }, [workspaceId]);

  useEffect(() => {
    if (workspaceId && selectedProjectId) {
      fetchProgressSteps();
    }
  }, [workspaceId, selectedProjectId]);

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const projectsData = response.data.projects || [];
      setProjects(projectsData);

      // Auto-select first project with progress enabled
      const progressEnabledProject = projectsData.find((p: Project) => p.isProgressEnabled);
      if (progressEnabledProject) {
        setSelectedProjectId(progressEnabledProject._id);
      } else if (projectsData.length > 0) {
        setSelectedProjectId(projectsData[0]._id);
      }

      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
      toast.error('Failed to load projects');
      setLoading(false);
    }
  };

  const fetchProgressSteps = async (isDataRefresh = false) => {
    if (!workspaceId || !selectedProjectId) return;

    try {
      if (isDataRefresh) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }
      const response = await progressStepService.getAll(workspaceId, selectedProjectId);
      setSteps(response.steps || []);
    } catch (err: any) {
      console.error('Error fetching progress steps:', err);
      toast.error('Failed to load progress steps');
    } finally {
      if (isDataRefresh) {
        setDataLoading(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleAdd = () => {
    setEditingStep(null);
    setShowModal(true);
  };

  const handleEdit = async (step: ProgressStep) => {
    try {
      setDataLoading(true);
      // Fetch full step details including image URLs
      const fullStep = await progressStepService.getById(workspaceId!, step._id);
      setEditingStep(fullStep);
      setShowModal(true);
    } catch (err: any) {
      console.error('Error fetching step details:', err);
      toast.error('Failed to load step details');
    } finally {
      setDataLoading(false);
    }
  };

  const handleDelete = (step: ProgressStep) => {
    setStepToDelete(step);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!stepToDelete) return;

    try {
      setFormLoading(true);
      setDataLoading(true);
      await progressStepService.delete(workspaceId!, stepToDelete._id);

      // Remove from local state immediately
      setSteps(prevSteps => prevSteps.filter(s => s._id !== stepToDelete._id));

      toast.success('Progress step deleted successfully');
      setShowDeleteModal(false);
      setStepToDelete(null);
    } catch (err: any) {
      console.error('Error deleting progress step:', err);
      toast.error(err.response?.data?.message || 'Failed to delete progress step');
      // Refresh on error to get correct state
      await fetchProgressSteps(true);
    } finally {
      setFormLoading(false);
      setDataLoading(false);
    }
  };

  const handleDragStart = (e: React.DragEvent<HTMLTableRowElement>, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget.innerHTML);
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent<HTMLTableRowElement>) => {
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '1';
    }
    setDraggedIndex(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent<HTMLTableRowElement>, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const draggedStep = steps[draggedIndex];

    // Reorder steps array locally for immediate UI update
    const newSteps = [...steps];
    const [removed] = newSteps.splice(draggedIndex, 1);
    newSteps.splice(dropIndex, 0, removed);

    // Update local state immediately for better UX
    setSteps(newSteps);
    setDraggedIndex(null);

    try {
      setDataLoading(true);

      // Determine direction based on indices
      const direction = draggedIndex < dropIndex ? 'down' : 'up';

      // Calculate how many times to move
      const moveCount = Math.abs(draggedIndex - dropIndex);

      // Make multiple API calls to move the step
      for (let i = 0; i < moveCount; i++) {
        await progressStepService.reorder(workspaceId!, selectedProjectId, draggedStep._id, direction);
      }

      // Fetch updated data to get correct step numbers
      await fetchProgressSteps(true);
    } catch (err: any) {
      console.error('Error reordering progress step:', err);
      toast.error(err.response?.data?.message || 'Failed to reorder progress step');
      // Revert to original order on error
      await fetchProgressSteps(true);
    } finally {
      setDataLoading(false);
    }
  };

  const handleModalClose = async () => {
    // Close modal immediately for better UX
    setShowModal(false);
    const currentEditingStep = editingStep;
    setEditingStep(null);

    // Refresh the step data in the background if we were editing
    if (currentEditingStep) {
      try {
        setDataLoading(true);
        const updatedStep = await progressStepService.getById(workspaceId!, currentEditingStep._id);
        setSteps(prevSteps =>
          prevSteps.map(s => s._id === updatedStep._id ? updatedStep : s)
        );
      } catch (err: any) {
        console.error('Error refreshing step:', err);
        // Refresh all on error
        await fetchProgressSteps(true);
      } finally {
        setDataLoading(false);
      }
    }
  };

  const handleModalSave = async (createdStep?: ProgressStep) => {
    if (createdStep) {
      // A new step was created, keep modal open and switch to edit mode
      try {
        const fullStep = await progressStepService.getById(workspaceId!, createdStep._id);
        setEditingStep(fullStep);

        // Add to local state immediately
        setSteps(prevSteps => [...prevSteps, createdStep].sort((a, b) => a.stepNumber - b.stepNumber));

        toast.success('You can now add photos and videos to this step');
      } catch (err: any) {
        console.error('Error fetching created step:', err);
        toast.error('Failed to load step details');
        setShowModal(false);
        setEditingStep(null);
        await fetchProgressSteps(true);
      }
    } else {
      // Step was updated, close modal
      try {
        setDataLoading(true);
        setShowModal(false);
        setEditingStep(null);

        // Refresh only the updated step data
        if (editingStep) {
          const updatedStep = await progressStepService.getById(workspaceId!, editingStep._id);
          setSteps(prevSteps =>
            prevSteps.map(s => s._id === updatedStep._id ? updatedStep : s)
          );
        }
      } catch (err: any) {
        console.error('Error refreshing step:', err);
        // Refresh all on error
        await fetchProgressSteps(true);
      } finally {
        setDataLoading(false);
      }
    }
  };

  const handleSettings = () => {
    if (selectedProjectId) {
      navigate(`/workspaces/${workspaceId}/projects/${selectedProjectId}/edit`);
    }
  };

  const selectedProject = projects.find(p => p._id === selectedProjectId);

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <div className="builders-container">
        {/* Section Header */}
        <div className="builders-section-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <h2 className="builders-section-title">
              {selectedProject?.progressLabelText || 'Project Progress'}
            </h2>
            <p className="builders-section-subtitle">
              Manage construction progress steps and updates
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
            <select
              className="form-input"
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              disabled={loading || projects.length === 0}
              style={{ minWidth: '280px' }}
            >
              <option value="">Select Project</option>
              {projects.map((project) => (
                <option key={project._id} value={project._id}>
                  {project.name}
                </option>
              ))}
            </select>
            <button
              className="btn btn-primary btn-md"
              onClick={handleAdd}
              disabled={!selectedProjectId || !selectedProject?.isProgressEnabled}
            >
              Add
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)'
        }}>
          {loading ? (
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          ) : !selectedProjectId ? (
            <div className="builders-empty">
              <p className="builders-empty-title">No project selected</p>
              <p className="builders-empty-text">
                Please select a project to manage progress steps
              </p>
            </div>
          ) : !selectedProject?.isProgressEnabled ? (
            <div className="builders-empty">
              <p className="builders-empty-title">Progress tracking is disabled</p>
              <p className="builders-empty-text">
                Enable progress tracking for this project in project settings
              </p>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSettings}
                style={{ marginTop: '16px' }}
              >
                Go to Settings
              </button>
            </div>
          ) : steps.length === 0 ? (
            <div className="builders-empty">
              <p className="builders-empty-title">No progress steps yet</p>
              <p className="builders-empty-text">
                Add your first progress step to start tracking construction milestones
              </p>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleAdd}
                style={{ marginTop: '16px' }}
              >
                Add First Step
              </button>
            </div>
          ) : (
            <div className="data-table-container" style={{ position: 'relative' }}>
              {dataLoading && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.7)',
                  zIndex: 10,
                  pointerEvents: 'none'
                }}>
                  <div className="builders-loading-spinner" />
                </div>
              )}
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}></th>
                    <th style={{ width: '80px' }}>Step</th>
                    <th style={{ width: '100px' }}>Gallery</th>
                    <th>Description</th>
                    <th style={{ width: '200px' }}>Time/Status</th>
                    <th style={{ width: '180px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody style={{
                  opacity: dataLoading ? 0.5 : 1,
                  transition: 'opacity 0.2s'
                }}>
                  {steps.map((step, index) => (
                    <tr
                      key={step._id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, index)}
                      style={{
                        cursor: 'move',
                        transition: 'opacity 0.2s'
                      }}
                    >
                      <td style={{
                        textAlign: 'center',
                        padding: '10px 8px'
                      }}>
                        <GripVertical size={18} style={{ color: '#9CA3AF' }} />
                      </td>
                      <td style={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: '#48182F'
                      }}>
                        {step.stepNumber}
                      </td>
                      <td style={{ textAlign: 'center', color: '#6B7280' }}>
                        {step.galleryCount || 0}
                      </td>
                      <td>
                        <div
                          dangerouslySetInnerHTML={{ __html: step.description || '' }}
                          style={{
                            maxHeight: '80px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        />
                      </td>
                      <td>
                        <div style={{ fontSize: '14px', color: '#48182F' }}>
                          {step.time || '—'}
                        </div>
                        <div style={{ fontSize: '13px', color: '#6B7280', marginTop: '4px' }}>
                          {step.status || '—'}
                        </div>
                      </td>
                      <td>
                        <div style={{
                          display: 'flex',
                          gap: '8px',
                          justifyContent: 'flex-end',
                          alignItems: 'center'
                        }}>
                          <button
                            className="btn btn-icon"
                            onClick={() => handleEdit(step)}
                            title="Edit"
                            style={{
                              width: '32px',
                              height: '32px',
                              padding: '6px',
                              backgroundColor: '#48182F',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            <Edit2 size={20} />
                          </button>
                          <button
                            className="btn btn-icon"
                            onClick={() => handleDelete(step)}
                            title="Delete"
                            style={{
                              width: '32px',
                              height: '32px',
                              padding: '6px',
                              backgroundColor: '#DC2626',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Progress Step Modal */}
      {showModal && (
        <ProgressStepModal
          workspaceId={workspaceId!}
          projectId={selectedProjectId}
          step={editingStep}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && stepToDelete && (
        <div className="contacts-modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="contacts-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="contacts-modal-title">Delete Progress Step</h2>
            <p className="contacts-modal-subtitle">
              Are you sure you want to delete step {stepToDelete.stepNumber}? This action cannot be undone.
            </p>
            <div className="contacts-modal-actions">
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowDeleteModal(false)}
                disabled={formLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-md"
                onClick={handleDeleteConfirm}
                disabled={formLoading}
              >
                {formLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default ProjectProgress;
